/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    /** Refresh regions outside .js-product, missed by the section-scoped swap. */
    static OUT_OF_SECTION_REGIONS = ['breadcrumbs', 'sticky-atc'];

    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector:not([data-selector-role="product-as-variants"])');
      this.data = this.getProductData();
      this.variants = this.data.combinedListing ? this.data.combinedListing.variants : this.data.product.variants;
      this.isProductFromCombinedListing = this.data.combinedListing ? true : false;
      this.variant = this.getSelectedVariant();
      // Monotonic counter identifying the most recent product switch. Async change
      // handlers are not serialized, so a slower response must not overwrite the
      // content of a switch that started after it.
      this.switchSequence = 0;

      // immediately update the url so the correct loyalty points are shown
      this.updateUrl(null, null);
      this.updateAvailableOptions();
      this.updateSelectedOptionPrice();
      this.updateAvailability();
      this.updateAddToCartButton();

      // Only update price if special price is out of sync
      const customerSpecialPriceEl = this.section.querySelector('[data-customer-special-price]');
      if (customerSpecialPriceEl && this.variant) {
        const currentSpecialPrice = customerSpecialPriceEl.innerHTML.trim();
        const correctSpecialPrice = this.data.formatted[this.variant.id]?.customerSpecialPrice || '';
        if (currentSpecialPrice !== correctSpecialPrice) {
          this.updatePrice();
        }
      }

      this.addEventListener('change', this.handleVariantChange.bind(this));
      document.addEventListener('on:quick-add:opened', this.updateAddToCartButton.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    async handleVariantChange(evt) {
      this.optionSelectors = this.querySelectorAll('.option-selector:not([data-selector-role="product-as-variants"])');

      if (evt.target.matches('.js-product-as-variant')) {
        await this.handleProductAsVariantChange(evt);
        return;
      }

      const pageOrigin = window.location.origin;
      this.variant = null;
      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      let productHandle = null;
      let productDataApi = null;

      if (this.isProductFromCombinedListing) {
        const originalProductId = this.data?.formatted[this.variant.id]?.originalProductId || this.variant?.featured_image?.product_id;
        productHandle = this.data?.formatted[this.variant.id]?.handle || '';
        if (!productHandle) {
          if (originalProductId) {
            productDataApi = await this.fetchVariantProduct(originalProductId);
            productHandle = productDataApi.data.product.handle;
          }
        }
      }

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt, productHandle);
        // Another variant of the same product (e.g. 6 or 12 pieces): the url has no ?variant=
        // for the loyalty app to follow, so the points are counted for the selected variant.
        if (!productHandle && !this.section.matches('quick-add-drawer')) {
          this.updateLoyaltyPoints(document, this.variant.id);
          this.updateOgPrice(this.variant);
        }
        if (productHandle) {
          this.switchSequence += 1;
          const requestToken = this.switchSequence;
          const refreshed = await this.fetchAndUpdateContent(`${pageOrigin}/products/${productHandle}`, requestToken);
          // Bail out on a failed refresh, or when a newer switch superseded this one:
          // applying state now would describe a product the page is not showing.
          if (!refreshed || requestToken !== this.switchSequence) return;
          this.reinitializePickerState();
        }
      }

      const productUrl = productHandle
        ? `${pageOrigin}/products/${productHandle}`
        : `${pageOrigin}${this.dataset.url || this.data?.product?.url || ''}`;
      this.applyVariantState({ linkUrl: productUrl, evt });
    }

    /**
     * Handles product-level switching rendered via product_as_variants.
     * Uses the same section fetch refresh flow as combined listings.
     * @param {object} evt - Event object.
     */
    async handleProductAsVariantChange(evt) {
      const productUrl = evt.target.dataset.productUrl;
      if (!productUrl) return;

      this.setProductAsVariantsBusy(true);

      this.switchSequence += 1;
      const requestToken = this.switchSequence;

      try {
        const pageOrigin = window.location.origin;
        const nextUrl = productUrl.startsWith('http') ? productUrl : `${pageOrigin}${productUrl}`;

        const refreshed = await this.fetchAndUpdateContent(nextUrl, requestToken);
        // Leave the current product intact if the refresh failed, or if the shopper
        // picked another product while this one was still loading.
        if (!refreshed || requestToken !== this.switchSequence) return;

        this.reinitializePickerState({ fallbackToAvailable: true });
        this.dataset.url = new URL(nextUrl, pageOrigin).pathname;
        this.applyVariantState({ linkUrl: nextUrl, syncUrl: true });
      } finally {
        // Only the newest request may unlock: a superseded one reaching here would
        // re-enable the selector while the switch that replaced it is still loading.
        if (requestToken === this.switchSequence) {
          this.setProductAsVariantsBusy(false);
        }
      }
    }

    /**
     * Locks the grouped-product selector while a switch is in flight. Attribute-only:
     * some inputs are permanently `disabled` for impossible combinations, so clearing
     * a disabled state here would wrongly re-enable them.
     * @param {boolean} isBusy - Whether a switch is in flight.
     */
    setProductAsVariantsBusy(isBusy) {
      if (isBusy) {
        this.setAttribute('aria-busy', 'true');
        this.setAttribute('data-pav-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
        this.removeAttribute('data-pav-busy');
      }
    }

    reinitializePickerState({ fallbackToAvailable = false } = {}) {
      this.optionSelectors = this.querySelectorAll('.option-selector:not([data-selector-role="product-as-variants"])');
      this.data = this.getProductData();
      this.variants = this.data.combinedListing ? this.data.combinedListing.variants : this.data.product.variants;
      this.isProductFromCombinedListing = !!this.data.combinedListing;

      // Assign on every path. Leaving this.variant untouched would carry the
      // previous product's variant id into the refreshed product's form.
      this.variant = this.getSelectedVariant()
        || (fallbackToAvailable
          ? this.variants?.find((v) => v.available) || this.variants?.[0] || null
          : null);

      this.dataset.url = this.data?.product?.url || this.dataset.url;
    }

    applyVariantState({ linkUrl, evt = null, syncUrl = false } = {}) {
      if (this.variant) {
        this.updateMedia();
        this.updateVariantInput();
      }

      this.updateAvailableOptions();
      this.updateAddToCartButton();
      this.updatePackageVolume();
      this.updatePackageVolume('package-volume-thermobox', 'packageVolumeThermobox');
      this.updateAvailability();
      this.updateTitle();
      this.updateLinks(linkUrl);
      this.updateStickyAtcPanel();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();

      if (syncUrl) {
        this.updateUrl(null, null);
      }

      if (evt) {
        VariantPicker.updateLabelText(evt);
      }

      this.dispatchVariantChange();
    }

    dispatchVariantChange() {
      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          isProductFromCombinedListing: this.isProductFromCombinedListing,
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }


    /**
     * Resolves lazy images inside markup that was just swapped in. main.js runs its
     * pass once at page load and exposes no re-init hook, so injected images would
     * keep their data- attributes forever. Mirrors setImageSources() in main.js.
     * @param {Element} region - Container whose contents were just replaced.
     */
    static loadLazyImagesIn(region) {
      region.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        const targets = img.parentNode && img.parentNode.tagName === 'PICTURE'
          ? Array.from(img.parentNode.children)
          : [img];

        targets.forEach((el) => {
          if (el.dataset.src && !el.src) el.src = el.dataset.src;
          if (el.dataset.srcset && !el.srcset) el.srcset = el.dataset.srcset;
        });
      });
    }

    /**
     * Shows or hides the sticky add-to-cart bar after a switch. The region is empty
     * when nothing is purchasable, and an empty bar would still slide in on scroll.
     */
    updateStickyAtcPanel() {
      if (this.section.matches('quick-add-drawer')) return;

      const region = document.querySelector('[data-refresh-with-variant="sticky-atc"]');
      if (!region) return;

      const panel = region.closest('sticky-atc-panel');
      if (!panel) return;

      panel.hidden = region.children.length === 0;
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;
      const variantAvailable = this.variant && this.getVariantAvailability();
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    updatePackageVolume(cartProperty = 'package-volume', variantProperty = 'packageVolume') {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) {
        return;
      }

      if (!this.variant) {
        return;
      }

      if (!this.productForm.getAttribute(cartProperty)) {
        return;
      }

      if (!this.data.formatted[this.variant.id][variantProperty]) {
        return;
      }

      this.productForm.setAttribute(cartProperty, this.data.formatted[this.variant.id][variantProperty]);
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available, price, compareAtPrice) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      const allVariantsUnavailable = optionEl.closest('[data-is-any-variant-available="false"]');
      if (allVariantsUnavailable) {
        available = true;
      }
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');
        const priceElement = el.querySelector('[data-custom-select-option-price]')

        if (priceElement && (price || compareAtPrice)) {
          const priceHtml = this.getPriceHtml(price, compareAtPrice);

          priceElement.innerHTML = priceHtml;
        }

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          priceElement.innerHTML = '';
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Determines availability for a variant using locationAvailable when configured,
     * falling back to the variantSoldOut metafield override, then Shopify's native available.
     */
    getFormattedVariantAvailability(variant) {
      const formattedVariant = this.data.formatted[variant.id];
      if (formattedVariant?.locationAvailable !== undefined) {
        return formattedVariant.locationAvailable;
      }
      if (formattedVariant?.variantSoldOut !== undefined) {
        return formattedVariant.variantSoldOut !== 'true';
      }
      return variant.available ?? false;
    }

    /**
     * Determines the availability of the current variant.
     */
    getVariantAvailability() {
      if (!this.variant) return null;
      return this.getFormattedVariantAvailability(this.variant);
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      const shouldUpdateOptionAvailability = this.dataset.showAvailability !== 'false';
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();
      const variantAvailable = this.variant && this.getVariantAvailability();
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
      const availabilityElement = this.section.querySelector('.product-availability');

      if (!this.variant) {
        currVariant = { options: selectedOptions };
      }

      if (availabilityElement.hasAttribute('data-restricted')) {
        // A product limited to certain customers keeps its own message, whatever the stock says.
      } else if (variantAvailable) {
        availabilityElement.classList.add('product-availability--available');
        availabilityElement.classList.remove('product-availability--unavailable');

        availabilityElement.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 21" width="20" height="21" fill="none">
              <path fill="currentColor" d="M10 .734c-5.514 0-10 4.486-10 10s4.486 10 10 10 10-4.486 10-10-4.486-10-10-10Zm0 18.182c-4.512 0-8.182-3.67-8.182-8.182 0-4.511 3.67-8.182 8.182-8.182 4.511 0 8.182 3.67 8.182 8.182 0 4.512-3.67 8.182-8.182 8.182Z"/>
              <path fill="currentColor" d="M13.75 7.199 8.607 12.34 6.25 9.984a.91.91 0 0 0-1.285 1.286l3 3a.91.91 0 0 0 1.286 0l5.784-5.786A.909.909 0 1 0 13.75 7.2Z"/>
          </svg>
          ${window.theme.strings.availability.available}
        `;
      } else {
        availabilityElement.classList.remove('product-availability--available');
        availabilityElement.classList.add('product-availability--unavailable');

        availabilityElement.innerHTML = unavailableStr;
      }

      if (!shouldUpdateOptionAvailability) return;

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  this.getFormattedVariantAvailability(variant),
                  this.data.formatted[variant.id].price,
                  this.data.formatted[variant.id].compareAtPrice
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            let variantPrice = null;
            let compareAtPrice = null;
            this.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                variantPrice = this.data.formatted[v.id].price;
                compareAtPrice = this.data.formatted[v.id].compareAtPrice;
                if (this.getFormattedVariantAvailability(v)) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable, variantPrice, compareAtPrice);
          });
        });
      }
    }

    /**
     * Updates the available options.
     */
    /**
 * Updates the available options.
 */
    updateAvailableOptions() {
      if (!this.data.availableOptions || !this.variant) {
        return;
      }

      const currentOptions = [];
      for (let i = 1; i <= 10; i++) {
        const optionValue = this.variant[`option${i}`];
        if (optionValue && optionValue !== 'Default Title') {
          currentOptions.push(optionValue);
        }
      }

      const optionElements = this.querySelectorAll('.js-option, .custom-select__option');
      const optionDimensions = this.data.availableOptions?.[0]?.length || 0;
      if (optionDimensions !== currentOptions.length) {
        optionElements.forEach((el) => el.removeAttribute('data-disabled'));
        return;
      }

      optionElements.forEach(el => {
        const value = el.getAttribute('value') || el.getAttribute('data-value');
        if (!value) return;

        // Guess which dimension this element belongs to by checking if its value appears in availableOptions
        let optionIndex = -1;
        for (let i = 0; i < 3; i++) { // assuming maximum 3 options (you can extend this to 10 if needed)
          if (this.data.availableOptions.some(opt => opt[i] === value)) {
            optionIndex = i;
            break;
          }
        }
        if (optionIndex === -1) {
          // Value not found in available options
          el.setAttribute('data-disabled', 'true');
          return;
        }

        const isAvailable = this.data.availableOptions.some(optionCombo => {
          // Check that the current selection matches, except at optionIndex where we test different values
          for (let i = 0; i < optionCombo.length; i++) {
            if (i === optionIndex) {
              // This is the option we are changing, so check if it matches the tested value
              if (optionCombo[i] !== value) {
                return false;
              }
            } else {
              // For other options, check if it matches the currently selected option
              if (currentOptions[i] && optionCombo[i] !== currentOptions[i]) {
                return false;
              }
            }
          }
          return true;
        });

        if (isAvailable) {
          el.removeAttribute('data-disabled');
        } else {
          el.setAttribute('data-disabled', 'true');
        }
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_image);
      } else {
        this.mediaGallery = this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the title.
     */
    updateTitle() {
      const title = this.section.querySelector('.product-title');

      if (title) {
        // Write into the element that holds the dynamic title text, not the whole
        // heading: the heading can also contain a weight span, which replacing its
        // contents would destroy. textContent because the serialised product title
        // is raw text, not markup.
        const titleText = title.querySelector('[data-product-title-text]')
          || title.querySelector('.js-prod-link')
          || title;
        titleText.textContent = this.data.product.title;
      }

      if (this.data.documentTitle) {
        document.title = VariantPicker.decodeHtmlEntities(this.data.documentTitle);
      }
    }

    /**
     * Decodes HTML entities in a string. Uses a detached textarea, whose content is
     * parsed as character data, so no markup in the input can execute.
     * @param {string} text - Possibly entity-encoded text.
     * @returns {string} Plain text.
     */
    static decodeHtmlEntities(text) {
      const el = document.createElement('textarea');
      el.innerHTML = text;
      return el.value;
    }

    /**
     * Updates the title.
     */
    updateLinks(linkUrl) {
      const links = this.section.querySelectorAll('.js-prod-link');

      links.forEach((link) => link.href = linkUrl);
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;
      if (this.variant) {
        const customerSpecialPriceEl = document.querySelector('[data-customer-special-price]');
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        this.updateSelectedOptionPrice();
        const discountPercentage = Math.round((this.variant.compare_at_price - this.variant.price) * 100 / this.variant.compare_at_price);
        const priceWasHtml = this.data.formatted[this.variant.id]?.compareAtPrice
          ? `<s>${this.data.formatted[this.variant.id].compareAtPrice}</s>
            <span class="mr-2">&nbsp;(-${discountPercentage}%)</span>`
          : '';
        // Update current price and original price if on sale.
        if (customerSpecialPriceEl) {
          customerSpecialPriceEl.innerHTML = this.data.formatted[this.variant.id].customerSpecialPrice;
        }
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = priceWasHtml;

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant;
      this.price.querySelector('.price__no-variant').hidden = this.variant;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url to the current product path, without a variant parameter.
     * @param {object} evt - Event object.
     * @param {string} productHandle - The handle (unique identifier) of the product.
     */
    updateUrl(evt, productHandle) {
      if (this.dataset.updateUrl === 'false') return;
      if (productHandle && productHandle !== 'undefined') {
        window.history.replaceState({}, '', productHandle);
      } else {
        const productPath = (this.dataset.url && this.dataset.url !== 'undefined')
          ? this.dataset.url
          : window.location.pathname;
        window.history.replaceState({}, '', productPath);
      }
    }

    /**
     * Sets an input value defensively.
     * Injected third-party scripts (analytics, form trackers) can replace the input
     * value setter and assume the assigned value is a string. Passing a number then
     * throws inside their setter, which would abort the rest of the variant update
     * and leave the previous variant id in the form.
     * @param {Element} input - Input element.
     * @param {string|number} value - Value to set.
     */
    static setInputValue(input, value) {
      const newValue = String(value);

      try {
        input.value = newValue;
      } catch (error) {
        console.warn('variant-picker: input value setter threw', error);
      }

      // Hidden inputs reflect the value content attribute, so this recovers the
      // value if a patched setter threw or altered it.
      if (input.value !== newValue) {
        input.setAttribute('value', newValue);
      }
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;

        VariantPicker.setInputValue(input, this.variant.id);

        const handleInput = form.querySelector('input[name="handle"]');
        if (handleInput) {
          VariantPicker.setInputValue(handleInput, this.data.product.handle);
        }
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];
      const optionSelectors = this.querySelectorAll('.option-selector:not([data-selector-role="product-as-variants"])');

      optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('[data-custom-select-selected-value]').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[data-refresh-with-variant="variant-picker-script"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();

      return this.variants.find(
        (v) => {
          const isDefaultTitleInOptions = v.options.some((option) => option === 'Default Title');
          const variantOptions = isDefaultTitleInOptions ? v.options.slice(0, -1) : v.options;

          return variantOptions.every((val, index) => val === selectedOptions[index])
        }
      )
    }

    updateSelectedOptionPrice() {
      const { price, compareAtPrice } = this.data.formatted[this.variant.id];
      const priceElements = this.section.querySelectorAll('[data-custom-select-selected-price]');
      const priceHtml = VariantPicker.getPriceHtml(price, compareAtPrice);

      priceElements.forEach((priceElement) => {
        priceElement.innerHTML = priceHtml;
      })
    }

    static getPriceHtml(price, compareAtPrice) {
      // Parse price strings like "1.090 Kč", "1 090,50 Kč", "1090 Kč"
      function parsePrice(str) {
        if (!str) return 0;
        // Remove all non-digit, non-separator characters
        let cleaned = str.replace(/[^\d.,-]/g, '').replace(/\s/g, '');

        // If both '.' and ',' exist: '.' is thousands, ',' is decimal
        if (cleaned.includes('.') && cleaned.includes(',')) {
          cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        }
        // If only '.' and matches thousands pattern (e.g. 1.090)
        else if (cleaned.includes('.') && /^\d{1,3}\.\d{3}$/.test(cleaned)) {
          cleaned = cleaned.replace(/\./g, '');
        }
        // If only ',' treat as decimal
        else if (cleaned.includes(',')) {
          cleaned = cleaned.replace(',', '.');
        }
        return parseFloat(cleaned);
      }

      const numericPrice = parsePrice(price);
      const numericCompareAtPrice = parsePrice(compareAtPrice);
      const hasDiscount = compareAtPrice && numericCompareAtPrice > numericPrice;
      const discountPercentage = hasDiscount
        ? Math.round(((numericCompareAtPrice - numericPrice) / numericCompareAtPrice) * 100)
        : null;

      return `
        ${hasDiscount ? `<s>${compareAtPrice}</s>` : ''}
        <strong class="${hasDiscount ? 'color-sale' : ''}">${price}</strong>
        ${discountPercentage ? `<span>(-${discountPercentage}%)</span>` : ''}
      `;
    }

    /**
     * Fetches a product's section markup and swaps it into the refreshable regions.
     * @param {string} url - Product URL to fetch.
     * @param {?number} requestToken - Value of this.switchSequence when the switch
     *   started. When it no longer matches, a newer switch has begun and this
     *   response is discarded instead of overwriting the newer product's content.
     * @returns {Promise<boolean>} True only when the content was actually replaced.
     */
    async fetchAndUpdateContent(url, requestToken = null) {
      const sectionSelector = document.querySelector('[data-product-section-id]');
      const section = document.querySelector('.product.js-product');
      const sectionId = sectionSelector?.dataset.productSectionId ?? section?.dataset.section;

      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('sections', sectionId);
      requestUrl.searchParams.set('add-assets', 'false');
      requestUrl.searchParams.set('lazy-load', 'false');

      let refreshed = false;

      await fetch(requestUrl.toString())
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(response => {
          if (requestToken !== null && requestToken !== this.switchSequence) return;

          const parser = new DOMParser();
          const html = parser.parseFromString(response[sectionId], 'text/html');
          // Scope to this picker's own product container. Selecting document-wide
          // would let a switch made inside the Quick Add drawer also rewrite the
          // PDP rendered behind it. Every refreshWithVariant region lives inside
          // a .js-product host, so this.section covers all of them.
          const oldSections = this.section.querySelectorAll('.refreshWithVariant');
          const newSections = html.querySelectorAll('.refreshWithVariant');
          if (this.section.matches('quick-add-drawer')) {
            const productBoxTag = html.querySelector('.product-box-tag');
            this.section.updateBoxTag(productBoxTag?.innerHTML);
          }
          const newSectionsMap = new Map();

          newSections.forEach(newSection => {
            const key = newSection.dataset.refreshWithVariant;
            if (key) {
              newSectionsMap.set(key, newSection);
            }
          });

          oldSections.forEach(oldSection => {
            const key = oldSection.dataset.refreshWithVariant;
            if (key && newSectionsMap.has(key)) {
              oldSection.innerHTML = newSectionsMap.get(key).innerHTML;
              VariantPicker.loadLazyImagesIn(oldSection);
              // The gallery's arrows, thumbnails and slides are new elements now, so its
              // handlers have to be attached to them again.
              if (typeof oldSection.refresh === 'function') oldSection.refresh();
            }
          });

          // Not a refresh region (the block renders itself), but its rows are built
          // from this script, so it has to describe the product that was switched to.
          const freshStores = html.querySelector('.js-store-availability-data');
          const liveStores = this.section.querySelector('.js-store-availability-data');
          if (freshStores && liveStores) liveStores.textContent = freshStores.textContent;

          // Swapped explicitly: these sit outside .js-product, and never from inside
          // the drawer, which has neither and would otherwise rewrite the PDP's.
          if (!this.section.matches('quick-add-drawer')) {
            VariantPicker.OUT_OF_SECTION_REGIONS.forEach((key) => {
              const replacement = newSectionsMap.get(key);
              const target = document.querySelector(
                `.refreshWithVariant[data-refresh-with-variant="${key}"]`
              );
              if (replacement && target) {
                target.innerHTML = replacement.innerHTML;
                VariantPicker.loadLazyImagesIn(target);
              }
            });
          }

          if (!this.section.matches('quick-add-drawer')) {
            this.updateLoyaltyPoints(html);
          }

          refreshed = true;
        })
        .catch(error => {
          console.error('Fetch error:', error);
        });

      if (!refreshed) return false;

      this.data = this.getProductData();
      this.updateSeoTags();
      return true;
    }

    /**
     * Sets the price in the social meta tag to the selected variant, as a page opened with that
     * variant would have it (other variants of the same product share every other tag).
     * @param {object} variant - The selected variant.
     */
    updateOgPrice(variant) {
      const meta = document.querySelector('meta[property="og:price:amount"]');
      if (!meta || !variant || typeof variant.price !== 'number') return;

      const rate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      const amount = (variant.price / 100 / rate).toFixed(2);
      // same decimal separator as the tag was rendered with
      meta.setAttribute('content', (meta.getAttribute('content') || '').includes(',') ? amount.replace('.', ',') : amount);
    }

    /**
     * Points the meta tags of the page head (description, canonical, og:* and twitter:*, price) and
     * the product structured data at the product now shown, using the data rendered with the fetched
     * product page. The page title itself is set from `documentTitle` in applyVariantState.
     */
    updateSeoTags() {
      if (this.dataset.updateUrl === 'false') return;
      const seoScript = document.querySelector('[data-refresh-with-variant="seo-data"]');
      if (!seoScript) return;

      let seo;
      try {
        seo = JSON.parse(seoScript.textContent);
      } catch (error) {
        return;
      }

      if (seo.pageTitle) {
        // page_title comes HTML-escaped from Liquid
        const pageTitle = new DOMParser().parseFromString(seo.pageTitle, 'text/html').documentElement.textContent;
        document.querySelector('meta[property="og:title"]')?.setAttribute('content', pageTitle);
        document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', pageTitle);
      }
      if (seo.description) {
        const description = new DOMParser().parseFromString(seo.description, 'text/html').documentElement.textContent;
        ['meta[name="description"]', 'meta[property="og:description"]', 'meta[name="twitter:description"]'].forEach((selector) => {
          document.querySelector(selector)?.setAttribute('content', description);
        });
      }
      if (seo.image) {
        document.querySelector('meta[property="og:image"]')?.setAttribute('content', seo.image.http);
        document.querySelector('meta[property="og:image:secure_url"]')?.setAttribute('content', seo.image.https);
        document.querySelector('meta[property="og:image:width"]')?.setAttribute('content', seo.image.width);
        document.querySelector('meta[property="og:image:height"]')?.setAttribute('content', seo.image.height);
      }
      if (seo.priceAmount) {
        document.querySelector('meta[property="og:price:amount"]')?.setAttribute('content', seo.priceAmount);
        document.querySelector('meta[property="og:price:currency"]')?.setAttribute('content', seo.priceCurrency);
      }
      if (seo.canonical) {
        document.querySelector('link[rel="canonical"]')?.setAttribute('href', seo.canonical);
        document.querySelector('meta[property="og:url"]')?.setAttribute('content', seo.canonical);
      }
      if (seo.structuredData) {
        const schemaScript = document.querySelector('script[data-product-schema]');
        if (schemaScript) schemaScript.textContent = JSON.stringify(seo.structuredData);
      }
    }

    /**
     * The loyalty app counts the points once, from the variant data of the product the page
     * was opened with, and follows later changes only through the ?variant= in the url. A switch
     * to another product has neither, so the number stayed that of the first product. The points
     * are counted here from the new product's own data, with the app's formula.
     * @param {Document} html - The fetched section of the product that was switched to, or the
     *   page itself when only the variant of the same product changed.
     * @param {number} [variantId] - The variant that is selected. Without it the first available
     *   variant of the product is counted.
     */
    updateLoyaltyPoints(html, variantId = null) {
      const container = document.querySelector('.ll__container[data-vip-tier-level]');
      const pointsElement = container && container.querySelector('.ll__points-earned');
      const variantsElement = html.querySelector('script[class^="ll__product-variant-data"]');
      const shopElement = document.querySelector('script[class^="ll__shop-data"]');
      if (!pointsElement || !variantsElement || !shopElement) return;

      let shop;
      let variants;
      try {
        shop = JSON.parse(shopElement.textContent);
        variants = JSON.parse(variantsElement.textContent).variants || [];
      } catch (error) {
        return;
      }

      const customerTags = (container.dataset.customerTags || '').split(',');
      const segment = (shop.customerSegments || []).find((item) => customerTags.includes(item.customerTag));
      const rule = (segment ? segment.earnRules || [] : shop.earnRules || []).find((item) => item.type === 'Place an order');
      if (!rule) return;

      const tier = Number(container.dataset.vipTierLevel || 0);
      const membershipId = Number(container.dataset.membershipId) || undefined;
      const pointsPerAction = (shop.membershipOptions && shop.membershipOptions.isEnabled && membershipId
          && rule.pointsPerActionMemberships && rule.pointsPerActionMemberships[membershipId])
        || (shop.vipTierOptions && shop.vipTierOptions.isEnabled && rule.pointsPerActionVipTiers && rule.pointsPerActionVipTiers[tier])
        || rule.pointsPerAction
        || 0;

      const variant = (variantId && variants.find((item) => Number(item.id) === Number(variantId)))
        || variants.find((item) => item.available);
      const price = ((variant && variant.price) || 0) / 100 / ((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1);
      const points = Math.floor(
        rule.actionConditionType === 'relative'
          ? Math.floor(price / (rule.actionConditionAmount || 1)) * pointsPerAction
          : pointsPerAction
      );

      if (!points) {
        container.style.display = 'none';
        return;
      }

      pointsElement.textContent = String(points);
      container.style.display = '';

      const nameElement = document.querySelector('[data-loyalty-points-name]');
      const names = window.theme && window.theme.strings && window.theme.strings.loyaltyPointsName;
      if (nameElement && names) {
        nameElement.textContent = points === 1 ? names.singular : points >= 5 ? names.genitivePlural : names.dualPlural;
      }
    }

    async fetchVariantProduct(productId) {
      try {
        const response = await fetch(`${window.location.origin}/api/2025-01/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': window.sga.settings.storefrontVariantPickerApiKey,
          },
          body: JSON.stringify({
            query: `
                query getProductById {
                  product(id: "gid://shopify/Product/${productId}") {
                    handle
                  }
                }
              `}),
        });

        if (!response.ok) {
          console.error('Network response was not ok', response);
          throw new Error('Network response was not ok');
        }

        const responseData = await response.json();
        return responseData;
      } catch (error) {
        console.error('Fetch GraphQL error:', error);
        throw error;
      }
    };
  }

  customElements.define('variant-picker', VariantPicker);
}
